import minBy from 'lodash/minBy';

import { GrowthFactorType } from '@watershed/constants/forecasts';

import { PlanTargetIntensityType } from '@watershed/constants/reductions';
import { BadInputError } from '@watershed/errors/BadInputError';
// eslint-disable-next-line no-restricted-imports
import { GQAggregateKind } from '../generated/graphql-schema-types';
import assertNever from '@watershed/shared-util/src/assertNever';

import { FilterFieldWatershed } from '../constants/filterField';
import { ReductionsContributions } from '../reductions/contributionsUtils';
import {
  BusinessCategory,
  isBusinessCategory,
} from '../utils/BusinessCategory';
import { IntensityDenominatorKind } from '../utils/intensityDenominators';
import must from '../utils/must';
import { YearMonth, YM, YMInterval } from '../utils/YearMonth';
import { BusinessMetrics, BusinessMetricsRow } from './BusinessMetrics';
import { TimeseriesKindIntensity } from './ForecastX';
import { GrowthFactorIdentifier } from './GrowthFactorIdentifier';
import {
  GrossProfitGrowthForecastConfig,
  GrowthForecastConfig,
  NightsStayedGrowthForecastConfig,
  OrdersGrowthForecastConfig,
  PatientsGrowthForecastConfig,
} from './GrowthForecastConfig';

export type TimeseriesIntensityType = Exclude<
  PlanTargetIntensityType,
  'SupplierEngagement' | 'SupplierEngagementBySpend' | 'RenewableElectricity'
>;

/**
 * This function makes sure that we have a continuous year of headcount
 * or revenue data on the reference period.
 */
export function hasGrowthFactorDataInForecastReferencePeriod(
  contributions: ReductionsContributions,
  forecastReferencePeriod: YMInterval
): boolean {
  const values = new Array<BusinessMetricsRow>();
  const actuals = Array.from(contributions.monthInfos.values()).map((v) => {
    return {
      date: YM.fromJSDate(v.date),
      headcount: v.headcount,
      revenue: v.revenue,
    };
  });

  const actualsStart = must(minBy(actuals, (a) => a.date)).date;

  actuals.forEach((actual) => {
    const offset = YM.diff(actual.date, actualsStart);
    // values is an array where the index represents how many months between
    // the start and current month
    values[offset] = {
      growthFactors: {
        [GrowthFactorType.Revenue]: actual.revenue,
        [GrowthFactorType.Headcount]: actual.headcount,
      },
      forecasted: false,
    };
  });
  let hasAllRevenueData = true;
  let hasAllHeadcountData = true;

  for (const month of forecastReferencePeriod.iter('month')) {
    const offset = YM.diff(month, actualsStart);
    const refPeriodMonthData: BusinessMetricsRow | undefined = values[offset];
    hasAllRevenueData =
      !!refPeriodMonthData?.growthFactors[GrowthFactorType.Revenue] &&
      hasAllRevenueData;
    hasAllHeadcountData =
      !!refPeriodMonthData?.growthFactors[GrowthFactorType.Headcount] &&
      hasAllHeadcountData;
  }
  return hasAllHeadcountData || hasAllRevenueData;
}

export function getProhibitedGrowthFactors(
  businessMetrics: BusinessMetrics,
  referencePeriodInterval: YMInterval
): Array<GrowthFactorIdentifier> {
  return businessMetrics.growthFactorsPresentForHistoricalInterval({
    interval: referencePeriodInterval,
  }).notCompletelyPresent;
}

// The visible interval in reductions must contain both the forecast reference
// period and the baseline year
export function calculateVisibleIntervalStart(
  forecastReferencePeriod: YMInterval,
  baselineYearStart: YearMonth
): YearMonth {
  return YM.min(forecastReferencePeriod.start, baselineYearStart);
}

// Create a special error class so we can just catch missing intensity errors
export class MissingIntensityError extends Error {}

/**
 * A filterable footprint field is a field that
 * - has a string or nullable string type (custom tags)
 * - overlaps with GQFilterFieldLegacy, the type we use to compose
 *   footprint filters
 */
export type FilterableFootprintContributionsFields =
  | FilterFieldWatershed
  | string;

export type SelectedBoundary = Map<
  FilterableFootprintContributionsFields,
  Array<string>
>;
export type PotentialBoundaries = Map<
  FilterableFootprintContributionsFields,
  Set<string>
>;

export const ALLOWED_BOUNDARY_FIELDS = [
  FilterFieldWatershed.categoryId,
  FilterFieldWatershed.subcategoryId,
  FilterFieldWatershed.location,
  FilterFieldWatershed.locationCountry,
  FilterFieldWatershed.vendor,
  FilterFieldWatershed.product,
  FilterFieldWatershed.buildingName,
  FilterFieldWatershed.electricityType,
  FilterFieldWatershed.description,
] as const;

const FILTER_FIELD_WATERSHED_TO_LABEL: Record<string, string> = {
  [FilterFieldWatershed.categoryId]: 'Category',
  [FilterFieldWatershed.subcategoryId]: 'Subcategory',
  [FilterFieldWatershed.locationCountry]: 'Country',
  [FilterFieldWatershed.locationCity]: 'City',
  [FilterFieldWatershed.locationState]: 'State',
  [FilterFieldWatershed.vendor]: 'Supplier',
  [FilterFieldWatershed.buildingName]: 'Building name',
  [FilterFieldWatershed.companyId]: 'Company ID',
  [FilterFieldWatershed.description]: 'Description',
  [FilterFieldWatershed.electricityType]: 'Electricity type',
  [FilterFieldWatershed.footprintKind]: 'Footprint kind',
  [FilterFieldWatershed.ghgCategoryId]: 'GHG Category',
  [FilterFieldWatershed.ghgScope]: 'GHG scope',
  [FilterFieldWatershed.inputUnit]: 'Input unit',
  [FilterFieldWatershed.location]: 'Location',
  [FilterFieldWatershed.isSupplierSpecific]: 'Is supplier specific',
  [FilterFieldWatershed.product]: 'Product',
};

/**
 * Returns a label to be used in the UI for a given filter field. If the field
 * is not covered by FILTER_FIELD_WATERSHED_TO_LABEL (for example, custom tags),
 * the simply return the field value as-is.
 */
export function getLabelForFilterField(
  field: FilterableFootprintContributionsFields
): string {
  const label = FILTER_FIELD_WATERSHED_TO_LABEL[field];
  if (label) {
    return label;
  }
  return field;
}

// Boundaries for target and initiatives cannot include ghg scope and category id
// because those are first class.
export function isFilterFieldValidBoundary(
  field: FilterableFootprintContributionsFields
): boolean {
  return field !== 'ghgCategoryId' && field !== 'ghgScope';
}

/**
 * TODO: We really need to consolidate intensity type, intensity kind, and aggregate kind
 * They all serve slightly different functions eg
 * - whether you can forecast on this,
 * - whether you can aggregate with this,
 * - whether you can set a target on this,
 * - whether you can set a SBT target on this,
 * but they should share the same types at least!
 */
export const INTENSITY_TYPE_TO_INTENSITY_KIND: Record<
  Exclude<
    PlanTargetIntensityType,
    | 'Absolute'
    | 'SupplierEngagement'
    | 'SupplierEngagementBySpend'
    | 'RenewableElectricity'
    | 'Custom'
  >,
  IntensityDenominatorKind
> = {
  [PlanTargetIntensityType.Headcount]: 'headcount',
  [PlanTargetIntensityType.Revenue]: 'revenue',
  [PlanTargetIntensityType.GrossProfit]: 'gross_profit',
  [PlanTargetIntensityType.NightsStayed]: 'nights_stayed',
  [PlanTargetIntensityType.Patients]: 'patients',
  [PlanTargetIntensityType.Orders]: 'orders',
};

export function timeseriesKindIntensityFromType(
  intensityType: TimeseriesIntensityType,
  config: GrowthForecastConfig | null
): TimeseriesKindIntensity {
  switch (intensityType) {
    case PlanTargetIntensityType.Absolute:
      return {
        kind: GQAggregateKind.Total,
      };
    case PlanTargetIntensityType.Headcount:
      return {
        kind: GQAggregateKind.HeadcountIntensity,
      };
    case PlanTargetIntensityType.Revenue:
      return {
        kind: GQAggregateKind.RevenueIntensity,
      };
    case PlanTargetIntensityType.GrossProfit:
      return {
        kind: GQAggregateKind.CustomIntensity,
        customIntensityConfig: GrossProfitGrowthForecastConfig,
      };
    case PlanTargetIntensityType.NightsStayed:
      return {
        kind: GQAggregateKind.CustomIntensity,
        customIntensityConfig: NightsStayedGrowthForecastConfig,
      };
    case PlanTargetIntensityType.Orders:
      return {
        kind: GQAggregateKind.CustomIntensity,
        customIntensityConfig: OrdersGrowthForecastConfig,
      };
    case PlanTargetIntensityType.Patients:
      return {
        kind: GQAggregateKind.CustomIntensity,
        customIntensityConfig: PatientsGrowthForecastConfig,
      };
    case PlanTargetIntensityType.Custom:
      BadInputError.invariant(
        config !== null,
        'Must provide a config for custom intensities'
      );
      return {
        kind: GQAggregateKind.CustomIntensity,
        customIntensityConfig: config,
      };
    default:
      assertNever(intensityType);
  }
}

export function categorizedEmissionsToBusinessCategories(
  categorizedEmissions: Array<{
    businessCategory: string;
  }>
): Array<BusinessCategory> {
  const businessCategories: Array<BusinessCategory> = [];
  for (const { businessCategory } of categorizedEmissions) {
    if (isBusinessCategory(businessCategory)) {
      businessCategories.push(businessCategory);
    } else {
      throw new BadInputError(
        `Found invalid business category ${businessCategory}`
      );
    }
  }
  return businessCategories;
}
